import { Box, Collapse } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import { useCompletionFocusFlash } from "utils/completionFocusFlashAnimation";
import usePrevious from "utils/usePrevious";
import WidgetHeaderAction from "./WidgetHeaderAction";
import WidgetResources from "./WidgetResources";
import WidgetSummary from "./WidgetSummary";

const widgetEnter = keyframes`
  from {
    opacity: 0;
    translate: -0.75rem 1rem;
  }
  to {
    opacity: 1;
    translate: 0 0;
  }
`;

type Props = {
  completedCount: number | undefined;
  isCompleted: boolean;
  onDismiss: () => void;
  onOpen: () => void;
  totalCount: number;
};

export default function SetupWidget({
  completedCount,
  isCompleted,
  onDismiss,
  onOpen,
  totalCount,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const previousCompletedCount = usePrevious(completedCount);
  const { startCompletionFocusFlash } = useCompletionFocusFlash({
    shadowColorConfig: {
      dropShadow: "green.600",
      innerRing: "green.600",
      outerGlow: "green.600",
    },
    targetRef: widgetRef,
  });

  useEffect(() => {
    if (
      completedCount !== undefined &&
      previousCompletedCount !== undefined &&
      completedCount > previousCompletedCount
    ) {
      startCompletionFocusFlash(false);
    }
  }, [completedCount, previousCompletedCount, startCompletionFocusFlash]);

  return (
    <Box
      ref={widgetRef}
      position="fixed"
      bottom={5}
      left={5}
      width="18rem"
      maxWidth="calc(100vw - 2.5rem)"
      zIndex="popover"
      background="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      boxShadow="lg"
      overflow="hidden"
      animation={`${widgetEnter} 400ms cubic-bezier(0.2, 0.9, 0.2, 1)`}
      transition="box-shadow 300ms, transform 200ms"
      _hover={{
        boxShadow: "2xl",
        "&:not([data-is-completed=true])": {
          transform: "scale(1.025)",
        },
      }}
      data-is-completed={isCompleted || undefined}
    >
      <Box position="relative" zIndex={1} cursor="pointer" p={4}>
        <WidgetSummary
          completedCount={completedCount}
          isCompleted={isCompleted}
          totalCount={totalCount}
        />

        <WidgetHeaderAction
          isCompleted={isCompleted}
          isExpanded={isExpanded}
          onOpen={onOpen}
          setIsExpanded={setIsExpanded}
        />
      </Box>

      <Collapse in={isCompleted && isExpanded} unmountOnExit>
        <WidgetResources onDismiss={onDismiss} />
      </Collapse>
    </Box>
  );
}
